/**
 * Wave 6.2 / 6.3 — Cart resolution-code per-origin validity matrix.
 *
 * Mirrors the backend (Bill, 2026-04-25 final taxonomy + Wave 6.3 addition):
 *
 *   | OriginRole              | Without artifact | With artifact                  |
 *   |-------------------------|------------------|--------------------------------|
 *   | fsr-self                | Cancelled / SE   | Withdrawn / AA / Processed / SE|
 *   | lobby-queue / -entry-*  | W / AA / P / C / SE | Withdrawn / AA / Processed / SE |
 *   | kiosk                   | (same as lobby)                                   |
 *   | rcp-portal              | W / AA / P / SE (Cancelled never valid for portal)|
 *
 * Wave 6.3 — `System-Error` (SE) is valid for ALL origin × artifact tuples.
 * Used when an external system failure required Ops cleanup (not a customer
 * or bank decision). Pairs with an `errorCode` from /v3/config/system-error-codes.
 *
 * The UI enforces this client-side as a first line of defense; the backend
 * validates the same matrix again on POST /v3/carts/{cartId}/resolve.
 *
 * `Open` is the default placeholder code and is never a valid manual
 * resolution choice — it represents an in-flight cart.
 */

import type {
  CartOriginRole,
  CartResolutionCode,
} from '@retail-ops/shared-types';

export interface ResolutionCodeOption {
  code: CartResolutionCode;
  label: string;
  /** One-line summary surfaced under the radio label. */
  description: string;
  /**
   * Visual variant used for the option card. Maps to existing PCB design
   * tokens — no new colors introduced.
   */
  tone: 'neutral' | 'caution' | 'positive' | 'danger';
}

const OPTION_DEFS: Record<Exclude<CartResolutionCode, 'Open'>, ResolutionCodeOption> = {
  Processed: {
    code: 'Processed',
    label: 'Processed',
    description: 'Account created, signed, and posted. Terminal success.',
    tone: 'positive',
  },
  Withdrawn: {
    code: 'Withdrawn',
    label: 'Withdrawn',
    description: 'Customer changed their mind, left, or declined to continue.',
    tone: 'caution',
  },
  'Adverse-Action': {
    code: 'Adverse-Action',
    label: 'Adverse Action',
    description: 'Bank declined. Generates the adverse-action letter and the compliance review entry.',
    tone: 'danger',
  },
  Cancelled: {
    code: 'Cancelled',
    label: 'Cancelled',
    description: 'Empty draft abandoned without customer interaction.',
    tone: 'neutral',
  },
  // Wave 6.3 — Ops cleanup after an external system failure.
  'System-Error': {
    code: 'System-Error',
    label: 'System Error',
    description:
      'Choose this when an external system failure required Ops cleanup — not a customer or bank decision.',
    tone: 'caution',
  },
};

/**
 * Returns the option list valid for the given origin tier + artifact state,
 * preserving display order. Empty result indicates a programming bug — the
 * caller should treat that as a "this cart cannot be resolved here" state.
 */
export function getValidResolutionOptions(
  originRole: CartOriginRole | undefined,
  hasRegulatedArtifact: boolean | undefined,
): ResolutionCodeOption[] {
  const role: CartOriginRole = originRole ?? 'fsr-self';
  const hasArtifact = hasRegulatedArtifact === true;

  // Wave 6.3 — System-Error is valid for ALL origin × artifact tuples.
  // It always renders LAST in the radio set so the primary close-flow paths
  // (Processed / Withdrawn / etc.) remain visually first.
  const SE = OPTION_DEFS['System-Error'];

  if (role === 'fsr-self') {
    return hasArtifact
      ? [OPTION_DEFS.Withdrawn, OPTION_DEFS['Adverse-Action'], OPTION_DEFS.Processed, SE]
      : [OPTION_DEFS.Cancelled, SE];
  }

  if (role === 'rcp-portal') {
    // Cancelled is never valid for rcp-portal regardless of artifact.
    return [OPTION_DEFS.Withdrawn, OPTION_DEFS['Adverse-Action'], OPTION_DEFS.Processed, SE];
  }

  // lobby-queue / lobby-entry-internal / lobby-entry-external / kiosk
  return hasArtifact
    ? [OPTION_DEFS.Withdrawn, OPTION_DEFS['Adverse-Action'], OPTION_DEFS.Processed, SE]
    : [
        OPTION_DEFS.Withdrawn,
        OPTION_DEFS['Adverse-Action'],
        OPTION_DEFS.Processed,
        OPTION_DEFS.Cancelled,
        SE,
      ];
}

/**
 * Humanize an origin tier for compliance-queue rows.
 *   fsr-self              → "Branch FSR"
 *   lobby-queue           → "Lobby"
 *   lobby-entry-internal  → "Lobby"
 *   lobby-entry-external  → "Lobby"
 *   kiosk                 → "Kiosk"
 *   rcp-portal            → "Customer portal"
 */
export function humanizeOriginTier(role: CartOriginRole | undefined): string {
  switch (role) {
    case 'fsr-self':
      return 'Branch FSR';
    case 'lobby-queue':
    case 'lobby-entry-internal':
    case 'lobby-entry-external':
      return 'Lobby';
    case 'kiosk':
      return 'Kiosk';
    case 'rcp-portal':
      return 'Customer portal';
    default:
      return 'Unknown';
  }
}

/**
 * The minimum reason length backend accepts. Mirrors the brief: ≥ 5 chars.
 */
export const MIN_REASON_LENGTH = 5;
